import type { LabelError, Result, SessionContext, Template, Warning, Zone } from '@tsagko/label-contracts'
import { useCallback, useEffect, useMemo, useRef, useState, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { getPrinterProfile, getPrinterProfiles } from '@tsagko/label-printer-profiles'
import { resolvePrintRotation } from '@tsagko/label-zpl-pipeline'
import { useAdapter } from '../adapters/AdapterProvider'
import { collectAllVariableNames } from '../block-configurator/variables-ops'
import { collectBakedInstances } from '../template-designer/block-instance-ops'
import { PrintDialog } from '../printing/PrintDialog'
import { DEMO_SCHEMA, flattenSchema } from '../shell/demo-schema'
import { CollapsedRail, panelBodyId, usePanelCollapsed } from '../ui/CollapsibleSection'
import { useToast } from '../ui/Toaster'
import { PREVIEW_PANEL_ID, PREVIEW_ZPL_PANEL_ID } from './panel-ids'
import { PreviewImage } from './PreviewImage'
import { PreviewToolbar } from './PreviewToolbar'
import { persistProfileId, resolveStoredProfileId } from './profile-storage'
import { ZplViewer } from './ZplViewer'
import { generateSampleValues, type SampleMode } from './sample-data'
import { useRender } from './useRender'

/**
 * Returns true when a ZPL string contains at least one printable body command
 * (^FO, ^GB, ^BC, ^BQ, ^GFA, ^BE, ^B7, ^BX, etc.). Used to skip the
 * auto-render request when the template has no renderable items, avoiding a
 * needless backend 500 (Neodynamic rejects a label with only setup/teardown).
 *
 * Detection is cheap: a single regex scan over the ZPL string. The set covers
 * every command the pipeline currently emits as well as common ZPL commands so
 * a manually-typed ZPL block also triggers a render. False negatives (custom ZPL
 * with unusual commands) result in a skipped render — consistent with idle copy.
 */
export function zplHasContent(zpl: string): boolean {
  // ^FO = field origin (text/image anchor), ^GB = graphic box (fill/border),
  // ^GF = graphic field (rasterised image), ^BC/^BE/^BQ/^B3/^B7/^BX/^BI/^BM =
  // barcode families. Any of these means there is something to print.
  return /\^(FO|GB|GF|BC|BE|BQ|B3|B7|BX|BI|BM)[^A-Z0-9]*/i.test(zpl)
}

/**
 * Synthesize UNRESOLVED_VARIABLE warnings for every baked-block-instance whose
 * expansion still contains whole-string `{name}` text leaves — bake leaves the
 * literal placeholder when both value and default are empty. Per-instance
 * attribution via `path: [baked.id]` keeps the warning dedupe distinct across
 * duplicate instances.
 *
 * `data` is the SAME record the preview passes to `generateZpl` (sample
 * values / active sample entity). The real render pipeline's `resolveContent`
 * (packages/zpl-pipeline/src/variables.ts) prefers `data[name]` over any
 * bake-time default when it resolves a surviving `{name}` placeholder at
 * render time — so a placeholder the preview's sample/entity data CAN
 * resolve will print correctly and must NOT be flagged here. Only a name
 * `data` has no entry for is genuinely unresolved (issue #132 item e).
 */
function synthesizeUnresolvedWarnings(rootZone: Zone, data: Record<string, unknown>): Warning[] {
  const warnings: Warning[] = []
  for (const { baked } of collectBakedInstances(rootZone)) {
    for (const name of collectAllVariableNames(baked.expansion)) {
      if (data[name] !== undefined) continue
      warnings.push({
        code: 'UNRESOLVED_VARIABLE',
        severity: 'warn',
        message: `"${name}" has no value or default and will print literally`,
        path: [baked.id],
      })
    }
  }
  return warnings
}

interface PreviewPaneProps {
  template: Template
  /**
   * When provided, the toolbar shows a toggle that collapses the whole
   * preview half (#189). Only the Template Designer passes it — it owns the
   * SplitPane flag that renders the collapsed strip to expand back from.
   */
  onCollapse?: () => void
  /**
   * The Template Designer's `usePanelCollapsed().toggleRef` for the preview
   * half, forwarded to the toolbar's toggle so focus survives the collapse
   * (the whole pane unmounts; SplitPane's strip toggle takes over).
   */
  collapseToggleRef?: Ref<HTMLButtonElement>
}

// Computed once at module scope (not per-render/per-effect) so the demo
// fallback branch of the `fields` memo below returns a referentially STABLE
// array across renders. Recomputing this inline inside the memo would hand
// back a fresh array every time `sessionContext` transitions (e.g. its
// one-time null → resolved-session flip on mount even when schemaMeta.fields
// stays empty), which would change `data`'s identity and re-fire the
// generateZpl/lintTemplate effect an extra, unintended time.
const DEMO_FIELDS = flattenSchema(DEMO_SCHEMA).map(entry => entry.field)

/**
 * Host sample dicts (e.g. Odoo product records) commonly carry null for
 * unset optional fields. The ZPL pipeline's resolveContent special-cases
 * undefined but NOT null, so a null spread over the generated values would
 * print the literal string "null". Dropping nullish entries lets the
 * generated per-field sample value show through instead.
 */
function stripNullish(dict: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(dict).filter(([, value]) => value != null))
}

export function PreviewPane({ template, onCollapse, collapseToggleRef }: PreviewPaneProps) {
  const adapter = useAdapter()
  const toast = useToast()
  const { t } = useTranslation()
  const zplPanel = usePanelCollapsed(PREVIEW_ZPL_PANEL_ID)
  const profiles = useMemo(() => getPrinterProfiles(), [])
  const [mode, setMode] = useState<SampleMode>('sample')
  // Persisted across reloads (issue #132): a plain useState here reset to
  // profiles[0] on every remount regardless of what the operator last
  // picked. resolveStoredProfileId validates the stored id against the
  // live catalogue, falling back to profiles[0] when it's missing or names
  // a retired profile.
  const [profileId, setProfileId] = useState(() => resolveStoredProfileId())
  const handleProfileChange = useCallback((id: string) => {
    setProfileId(id)
    persistProfileId(id)
  }, [])

  // SessionContext drives two things: which fields feed the sample-value
  // generator, and whether the sample-entity picker is shown at all
  // (capabilities.features includes 'sample-data' — true only for hosts
  // wired to a real product catalog, e.g. the embedded Odoo adapter).
  // Fetched defensively: an adapter mock/stub that doesn't implement
  // getSessionContext (resolves to undefined) must not crash the pane —
  // it just means no session context yet, so we fall back to the demo
  // schema and hide the entity picker.
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!adapter) return
      const result = await adapter.operations.getSessionContext()
      if (!cancelled && result && result.status === 'ok') {
        setSessionContext(result.data)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adapter])

  // The mounted schema: prefer the real SessionContext.schemaMeta.fields
  // when the host provided one (embedded adapter), otherwise fall back to
  // the built-in demo schema — this is what keeps demo-mode previews
  // rendering exactly as before.
  const fields = useMemo(() => {
    const schemaFields = sessionContext?.schemaMeta.fields ?? []
    return schemaFields.length > 0 ? schemaFields : DEMO_FIELDS
  }, [sessionContext])

  const sampleDataEnabled = sessionContext?.capabilities.features.includes('sample-data') ?? false

  // A looked-up real entity (via the toolbar's sample-entity search)
  // overrides the synthesized sample values for the 'sample' tier only —
  // switching to 'empty'/'torture' must show boundary values, not a stale
  // real entity.
  const [activeSampleEntity, setActiveSampleEntity] = useState<Record<string, unknown> | null>(
    null
  )
  const [searchPending, setSearchPending] = useState(false)
  // Set when a search's code lookup AND its name-lookup fallback both miss
  // (issue #128) — surfaces a persistent banner so the operator sees that
  // the canvas is still showing synthesized/demo values, rather than the
  // silent fallback the toast-only feedback left previously.
  const [searchNotFound, setSearchNotFound] = useState(false)
  // Monotonic generation for sample-entity searches: bumped by every new
  // search AND by mode changes; a response may only touch state while its
  // generation is still current. This is what discards a slow response
  // overtaken by a newer search, and stops a search resolving after a mode
  // change from reviving a stale entity. The AbortController additionally
  // cancels the superseded request for real adapters (same pattern as
  // useRender) — the generation check is the actual guard for adapters
  // that ignore the signal.
  const searchGenRef = useRef(0)
  const searchCtlRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Mode change invalidates both the active entity and any search still
    // in flight; pending is force-cleared because the invalidated search's
    // own (generation-guarded) clear will no longer run.
    searchGenRef.current += 1
    searchCtlRef.current?.abort()
    searchCtlRef.current = null
    setSearchPending(false)
    setSearchNotFound(false)
    setActiveSampleEntity(null)
  }, [mode])

  // Abort an in-flight search on unmount (mirrors useRender's cleanup).
  // The generation bump also neutralizes adapters that ignore the signal
  // and settle after unmount — their late Result fails the generation
  // check instead of toasting into the app-level Toaster.
  useEffect(
    () => () => {
      searchGenRef.current += 1
      searchCtlRef.current?.abort()
    },
    []
  )

  const handleSampleEntitySearch = useCallback(
    async (query: string) => {
      if (!adapter) return
      const generation = ++searchGenRef.current
      searchCtlRef.current?.abort()
      const ctl = new AbortController()
      searchCtlRef.current = ctl
      setSearchPending(true)
      setSearchNotFound(false)
      let result: Result<Record<string, unknown>>
      try {
        // Live SKUs commonly have no material code (issue #128) — a single
        // input is offered for both, so try `code` first (the historical,
        // more selective key) and only fall back to `name` on a definite
        // miss. The bridge ANDs code+name when both are sent, so the two
        // lookups must stay separate requests, never a combined query.
        result = await adapter.operations.sampleData(
          { kind: 'product', query: { code: query } },
          ctl.signal
        )
        if (
          generation === searchGenRef.current &&
          result.status === 'error' &&
          result.error.code === 'NOT_FOUND'
        ) {
          result = await adapter.operations.sampleData(
            { kind: 'product', query: { name: query } },
            ctl.signal
          )
        }
      } catch {
        // The embedded transport REJECTS (DOMException 'AbortError') when a
        // request is aborted — pre-send or mid-flight — rather than
        // resolving an error Result. Cancellation is never worth a toast.
        // Our own aborts (new search / mode change / unmount) always bump
        // the generation first, so the still-current case only covers an
        // adapter that throws spontaneously — clear the pending flag so
        // the toolbar doesn't wedge disabled.
        if (generation === searchGenRef.current) setSearchPending(false)
        return
      }
      // Stale response (a newer search started, or the mode changed while
      // this one was in flight): discard entirely — no state, no toasts.
      if (generation !== searchGenRef.current) return
      setSearchPending(false)
      if (result.status === 'ok') {
        setActiveSampleEntity(stripNullish(result.data))
      } else if (result.error.code === 'NOT_FOUND') {
        // Both the code lookup and the name-lookup fallback missed — clear
        // any real entity a PRIOR search in this session had resolved
        // (leaving it would let the banner below claim sample data is
        // showing while the canvas kept stale real-entity fields), so the
        // sample/demo values in `data` are the only ones that can show.
        setActiveSampleEntity(null)
        setSearchNotFound(true)
        toast.error(t('preview.sampleEntity.notFound', { query }))
      } else {
        toast.error(t('preview.sampleEntity.lookupFailed', { message: result.error.message }))
      }
    },
    [adapter, toast, t]
  )

  const data = useMemo(
    () => ({
      ...generateSampleValues(fields, mode),
      ...(mode === 'sample' ? activeSampleEntity : null),
    }),
    [fields, mode, activeSampleEntity]
  )

  const [zpl, setZpl] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<Warning[]>([])
  const [genError, setGenError] = useState<LabelError | undefined>()
  const [printDialogOpen, setPrintDialogOpen] = useState(false)

  // Synthesize designer-side UNRESOLVED_VARIABLE warnings by scanning the
  // template's baked instances for expansion text leaves still holding `{name}`
  // placeholders that the current preview data (sample/entity) can't resolve
  // either — see synthesizeUnresolvedWarnings' doc comment (#132 item e).
  const unresolvedWarnings = useMemo(
    () => synthesizeUnresolvedWarnings(template.rootZone, data),
    [template.rootZone, data]
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!adapter || !profileId) return
      const [gen, lint] = await Promise.all([
        adapter.operations.generateZpl({
          template,
          data,
          printerProfileId: profileId,
        }),
        adapter.operations.lintTemplate({
          template,
          printerProfileId: profileId,
        }),
      ])
      if (cancelled) return
      if (gen.status === 'ok') {
        setZpl(gen.data.zpl)
        setGenError(undefined)
      } else {
        // Keep the previous zpl: nulling it resets useRender (request → null)
        // which would wipe the last-good blobUrl — the banner + dimmed image
        // below communicate the staleness instead. First-gen failure leaves
        // zpl at its initial null, so the idle copy still shows then.
        setGenError(gen.error)
      }
      const genWarnings = gen.status === 'ok' ? gen.data.warnings : []
      const lintWarnings = lint.status === 'ok' ? lint.data.warnings : []
      const seen = new Set<string>()
      const merged: Warning[] = []
      // unresolvedWarnings are merged first so pipeline warnings can override/dedup
      for (const w of [...unresolvedWarnings, ...lintWarnings, ...genWarnings]) {
        const key = `${w.code}|${(w.path ?? []).join('.')}|${w.message}`
        if (seen.has(key)) continue
        seen.add(key)
        merged.push(w)
      }
      setWarnings(merged)
    })()
    return () => {
      cancelled = true
    }
  }, [adapter, template, data, profileId, unresolvedWarnings])

  // Print-time rotation (issue #133): computed client-side via the SAME
  // pure function the pipeline itself calls inside generateZpl — zero
  // drift risk, and (unlike the LABEL_ROTATED warning) available
  // synchronously from template+profile alone, with no round-trip.
  const profile = useMemo(() => getPrinterProfile(profileId), [profileId])
  const rotation = useMemo(
    () => (profile ? resolvePrintRotation(template, profile) : { deg: 0 as const, direction: 'cw' as const }),
    [template, profile]
  )
  const isRotated = rotation.deg !== 0

  const { status, blobUrl, error, renderNow } = useRender({
    zpl,
    printerProfileId: profileId,
    widthMm: template.widthMm,
    heightMm: template.heightMm,
    // Physical (rotated) dims when the label auto-rotates, so the render
    // request, Neodynamic's canvas, and the response's pixel arithmetic
    // agree with the ZPL body's own actual (physical) dimensions.
    rotated: isRotated,
    // Skip the auto-render when the ZPL has no printable commands. An empty
    // template produces only ^XA…^XZ which Neodynamic 500s on. Manual render
    // (renderNow) still fires regardless of this flag.
    skipAutoRender: zpl !== null && !zplHasContent(zpl),
  })

  return (
    <aside
      data-testid="preview-pane"
      className="flex h-full min-h-0 flex-col border-t border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
    >
      <PreviewToolbar
        mode={mode}
        onModeChange={setMode}
        profileId={profileId}
        onProfileChange={handleProfileChange}
        profiles={profiles.map(p => ({ id: p.id, name: p.name }))}
        onRender={renderNow}
        onPrint={() => setPrintDialogOpen(true)}
        printEnabled={zpl != null && !genError}
        sampleEntityEnabled={sampleDataEnabled}
        onSampleEntitySearch={handleSampleEntitySearch}
        searchPending={searchPending}
        onCollapse={onCollapse}
        collapseToggleRef={collapseToggleRef}
      />
      {genError && (
        <div
          data-testid="preview-gen-error"
          className="bg-rose-50 px-4 py-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300"
        >
          <span className="font-semibold">[{genError.code}]</span> {genError.message}
        </div>
      )}
      {searchNotFound && (
        <div
          data-testid="preview-sample-entity-not-found"
          className="bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {t('preview.sampleEntity.notFoundHint')}
        </div>
      )}
      <div id={panelBodyId(PREVIEW_PANEL_ID)} data-testid="preview-body" className="flex flex-1 min-h-0">
        <div
          className={`relative flex-1 min-w-0 border-r border-stone-200 dark:border-stone-800${genError && blobUrl ? ' opacity-50' : ''}`}
        >
          <PreviewImage
            status={status}
            blobUrl={blobUrl}
            error={error}
            rotationDeg={rotation.deg}
          />
          {isRotated && (
            <div
              data-testid="preview-rotation-badge"
              className="absolute left-2 top-2 z-10 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 shadow dark:bg-amber-900 dark:text-amber-100"
            >
              ↻ {t(`preview.rotationBadge.${rotation.direction}`)}
            </div>
          )}
        </div>
        {/* Collapsed (#189): the ZPL column becomes a rail on the right and
            its half of the preview body goes to the raster, which is the
            `flex-1 min-w-0` sibling in this row. */}
        {zplPanel.collapsed ? (
          <CollapsedRail
            panelId={PREVIEW_ZPL_PANEL_ID}
            label={t('preview.zpl.panelLabel')}
            onExpand={zplPanel.toggle}
            side="right"
            toggleRef={zplPanel.toggleRef}
          />
        ) : (
          <div className="flex-1 min-w-0">
            <ZplViewer
              zpl={zpl}
              warnings={warnings}
              onCollapse={zplPanel.toggle}
              collapseToggleRef={zplPanel.toggleRef}
            />
          </div>
        )}
      </div>
      <PrintDialog
        template={template}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </aside>
  )
}
